import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import queryString from 'query-string'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getLineSubscriptionFailureMessage } from '../../../utils/getLineSubscriptionFailureMessage'

interface BindingSnackbarProps {
  openSnackbar: (props: SnackbarVariantsProps) => void
}

export function useBindingSnackbar(props: BindingSnackbarProps) {
  const { t } = useTranslation()
  const { openSnackbar } = props
  const search = useMemo(() => queryString.parse(window.location.search), [])

  const snackbarPayloadMap = useMemo(() => ({
    lineSubscription: {
      success: t('login.subscriptionLineSuccess'),
      fail: getLineSubscriptionFailureMessage(t, 'login', Number(search.code)),
    },
    lineUnsubscribed: {
      success: t('login.lineUnscbscribeSuccess'),
      fail: t('login.lineUnscbscribeFail'),
    },
    lineBinding: {
      success: t('login.bindThirtyPartySuccess', { type: 'LINE' }),
      fail: t('login.bindThirtyPartyFail', { type: 'LINE' }),
    },
    facebookBinding: {
      success: t('login.bindThirtyPartySuccess', { type: 'Facebook' }),
      fail: t('login.bindThirtyPartyFail', { type: 'Facebook' }),
    },
    googleBinding: {
      success: t('login.bindThirtyPartySuccess', { type: 'Google' }),
      fail: t('login.bindThirtyPartyFail', { type: 'Google' }),
    },
    emailBinding: {
      success: t('login.bindEmailSuccess'),
      fail: t('login.bindEmailFail'),
    },
    smsBinding: {
      success: t('login.bindSmsSuccess'),
      fail: t('login.bindSmsFail'),
    },
    unbindSms: {
      success: t('login.unbindSmsSuccess'),
      fail: t('login.unbindSmsFail'),
    },
    unbindLine: {
      success: t('login.unbindThirtyPartySuccess', { type: 'LINE' }),
      fail: t('login.unbindThirtyPartyFail', { type: 'LINE' }),
    },
    unbindEmail: {
      success: t('login.unbindEmailSuccess'),
      fail: t('login.unbindEamilFail'),
    },
    unbindFacebook: {
      success: t('login.unbindThirtyPartySuccess', { type: 'Facebook' }),
      fail: t('login.unbindThirtyPartyFail', { type: 'Facebook' }),
    },
    unbindGoogle: {
      success: t('login.unbindThirtyPartySuccess', { type: 'Google' }),
      fail: t('login.unbindThirtyPartyFail', { type: 'Google' }),
    },
  }), [t, search])

  const showSnackbarBase = useCallback((type: keyof typeof snackbarPayloadMap) => {
    if (search[type] === undefined) return
    if (search[type] === 'true') {
      openSnackbar({
        message: snackbarPayloadMap[type].success,
        severity: 'success',
      })
    }
    else if (search[type] === 'false') {
      openSnackbar({
        message: snackbarPayloadMap[type].fail,
        severity: 'error',
      })
    }
    window.history.replaceState(null, '', window.location.pathname)
  }, [search, openSnackbar, snackbarPayloadMap])

  useEffect(() => {
    showSnackbarBase('lineSubscription')
    showSnackbarBase('lineUnsubscribed')
    showSnackbarBase('lineBinding')
    showSnackbarBase('facebookBinding')
    showSnackbarBase('googleBinding')
    showSnackbarBase('emailBinding')
    showSnackbarBase('smsBinding')
    showSnackbarBase('unbindSms')
    showSnackbarBase('unbindLine')
    showSnackbarBase('unbindEmail')
    showSnackbarBase('unbindFacebook')
    showSnackbarBase('unbindGoogle')
  }, [showSnackbarBase])
}
